import React, { useEffect, useState } from "react";
import IconButton from "../../../shared/ReusableComponents/IconButton";
import { GridTable } from "../../../shared/ReusableComponents/GridTable";
import { GridColDef } from "@mui/x-data-grid";
import { CommonFunctions } from "../../../shared/utils/CommonFunctions";
import { ActionButtons, Heading } from "../../../shared/ReusableComponents";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../../store/currentPreferencesSlice";
import PrescriptionForm from "./PrescriptionForm";
import { IMasterTemplateMedication } from "../../../services/interfaces/DataInterfaces";
import useGet from "../../../services/hooks/useGet";
import {
  apiEndpoints,
  notificationActions,
  notificationMessages,
} from "../../../shared/constants/api-constant";
import useDelete from "../../../services/hooks/useDelete";
import { ICONS, MedicineType } from "../../../shared/constants/enums";
import { Constants, HtmlHelpers } from "../../../shared/constants/all-constant";
import ConfirmDialog from "../../../shared/ReusableComponents/ConfirmDialog";
import { AppConfigurationHelper } from "../../../services/AppConfigurationHelper";

export const AdminMedications = ({ templateId }: { templateId: number }) => {
  const columns: GridColDef[] = [
    {
      field: "offset",
      headerName: "Offset",
      valueGetter: (value: any, row) =>
        value === 0
          ? Constants.DEFAULT_OFFSET_VALUE
          : CommonFunctions.convertMinutesToOffset(value),
    },
    {
      field: "medicineType",
      headerName: "Medicine Type",
      valueGetter: (value) =>
        value === MedicineType.VARIABLE ? "Variable" : value,
    },
    {
      field: "medicineName",
      headerName: "Medicine",
    },
    {
      field: "route",
      headerName: "Route",
    },
    {
      field: "dose",
      headerName: "Dose",
    },
    {
      field: "remarks",
      headerName: "Dose Remarks",
      valueGetter: (value, row) => CommonFunctions.getDisplayValue(row.remarks),
    },
    {
      field: "maxPRN",
      headerName: "Max PRN (Dose/24 hrs)",
      valueGetter: (value, row) => CommonFunctions.getDisplayValue(row.maxPRN),
    },
    {
      field: "frequencyName",
      headerName: "Frequency",
    },
    {
      field: "indication",
      headerName: "Indication",
    },
    {
      field: "prescribedBy",
      headerName: "Prescribed By",
      valueGetter: (value, row) => {
        const name = row.prescribedBy;
        const displayName = CommonFunctions.getDisplayValue(name);
        return displayName ===
          AppConfigurationHelper.Instance().displayDefaults?.emptyFallback
          ? displayName
          : CommonFunctions.formatName(displayName, {
              prefixKey: "doctor",
            });
      },
    },
    ...(CommonFunctions.IsCurrentSuperUser()
      ? [
          {
            field: "action",
            width: 120,
            resizable: false,
            headerName: "Action",
            renderCell: (params: any) => (
              <ActionButtons
                onEdit={() => openDailogPrescrition(params.row)}
                onDelete={() => handleDelete(params.row)}
              />
            ),
          },
        ]
      : []),
  ];

  const [medicationsData, setMedicationsData] = useState<
    IMasterTemplateMedication[]
  >([]);
  const [openDailog, setOpenDailog] = useState(false);
  const [selectedMedication, setSelectedMedication] =
    useState<IMasterTemplateMedication | null>(null);
  const [open, setOpen] = useState(false);
  const [editData, setEditData] = useState<string | null>("");
  const { data, getService, loading } = useGet();
  const { deleteService } = useDelete();
  const dispatch = useDispatch();

  const openDailogPrescrition = (data: any) => {
    setOpen(true);
    setEditData(data);
  };

  const handleDelete = (medication: any) => {
    setSelectedMedication(medication);
    setOpenDailog(true);
  };

  const confirmDelete = () => {
    if (selectedMedication) {
      dispatch(showLoader());
      const medicineName = CommonFunctions.truncateText(
        selectedMedication.medicineName,
        25
      );
      deleteService(
        `${apiEndpoints.TEMPLATE_MEDICATION}/${selectedMedication.id}`,
        {},
        `${medicineName} ${selectedMedication?.medicineType} Medicine has been Successfully Deleted.`
      )
        .then(() => {
          getService(`${apiEndpoints.TEMPLATEMEDICATIONS}/${templateId}`);
        })
        .then(() => dispatch(hideLoader()));
    }
    setOpenDailog(false);
    setSelectedMedication(null);
  };

  const closeDialogBox = (isSubmitted: boolean) => {
    isSubmitted &&
      getService(`${apiEndpoints.TEMPLATEMEDICATIONS}/${templateId}`);
    setOpen(false);
    setEditData(null);
  };

  useEffect(() => {
    getService(`${apiEndpoints.TEMPLATEMEDICATIONS}/${templateId}`);
  }, []);

  useEffect(() => {
    data && setMedicationsData(data);
  }, [data]);

  useEffect(() => {
    loading && dispatch(showLoader());
    !loading && dispatch(hideLoader());
  }, [loading]);

  return (
    <div style={{ backgroundColor: "#fff", padding: 0 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginLeft: "18px",
        }}
      >
        <div style={{ fontSize: "20px" }}>
          <span style={{ fontWeight: 700 }}>Prescription</span>
          {CommonFunctions.IsCurrentSuperUser() && (
            <IconButton
              sx={{
                ml: "10px",
                p: "5px 10px",
                height: "35px",
                textTransform: "capitalize",
                fontSize: "16px",
                columnGap: "8px",
                fontWeight: 700,
              }}
              text="Add New"
              onClick={(e) => {
                setOpen(true);
                setEditData(null);
              }}
              icon={
                <span className="material-symbols-outlined">add_circle</span>
              }
            />
          )}
          {open && (
            <PrescriptionForm
              templateId={templateId}
              open={open}
              editData={editData || {}}
              onClick={closeDialogBox}
            />
          )}
        </div>
      </div>
      <GridTable
        rows={medicationsData}
        columns={columns}
        pageSizeOptions={[5, 10, 15]}
        initialPageSize={10}
      />

      <ConfirmDialog
        dialogOpen={openDailog}
        setDialogOpen={() => setOpenDailog(false)}
        handleConfirm={confirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        icon={ICONS.DELETE}
        className="delete-icon"
        width="xs"
        text={
          <>
            <Heading
              text={`${notificationMessages.DELETE_RECORD_CONFIRMATION} ${notificationActions.PRESCRIPTION} ?`}
              isBold
              font="1.25rem"
            />
            <span
              className="medName-caption"
              title={selectedMedication?.medicineName}
            >
              “
              {CommonFunctions.truncateText(
                selectedMedication?.medicineName
                  ? selectedMedication?.medicineName
                  : "",
                25
              )}
              ”
            </span>
          </>
        }
      />
    </div>
  );
};
